/**
 * t611u: an organisation's own wording for the hint and help lines on settings pages (page subtitles, card intros,
 * small helper and footer lines). Each line has a stable key in code, `<group>.<item>.<slot>` (the settings section it
 * sits on, then which line), e.g. `account.kiosk.intro`; the default stays in the code and is shown when nothing is saved.
 * Plain text only: the apps render it as text (never HTML), keeping line breaks.
 */

/** The most characters one hint may hold. */
export const TEXT_OVERRIDE_MAX = 500;
/** The most customised hints one workspace may keep. */
export const TEXT_OVERRIDE_LIMIT = 400;

const KEY_RE = /^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+){2,4}$/;

/** `<group>.<item>.<slot>` (up to five parts), lower case, digits and hyphens, at most 80 characters. */
export function isTextOverrideKey(key: unknown): key is string {
  return typeof key === 'string' && key.length <= 80 && KEY_RE.test(key);
}

/**
 * Tidies what an owner typed: Windows line ends and control characters go, spaces at either end go, more than one
 * blank line becomes one. Returns the text, or an error to show.
 */
export function normaliseOverrideText(raw: unknown): { text: string } | { error: string } {
  if (typeof raw !== 'string') return { error: 'Type the text for this hint' };
  // eslint-disable-next-line no-control-regex
  const text = raw.replace(/\r\n?/g, '\n').replace(/[\u0000-\u0009\u000B-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2066-\u2069]/g, '')
    .split('\n').map(l => l.replace(/\s+$/, '')).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  if (!text) return { error: 'Type the text for this hint, or reset it to the default' };
  if ([...text].length > TEXT_OVERRIDE_MAX) return { error: `Keep it to ${TEXT_OVERRIDE_MAX} characters` };
  return { text };
}

export interface TextOverride {
  key: string;
  text: string;
  updated_at: string;
  updated_by: string | null;
  updated_by_name: string | null;
}

/** GET /workspaces/:ws/text-overrides (and what PUT / DELETE return). `can_edit`: the caller is an Owner. */
export interface TextOverridesView {
  overrides: TextOverride[];
  can_edit: boolean;
}

/** The settings section a key sits on (`account/kiosk` for `account.kiosk.intro`). */
export function textOverrideSection(key: string): { group: string; item: string } {
  const [group = '', item = ''] = key.split('.');
  return { group, item };
}
